export interface AesGcmEncryptionResult {
  encryptedData: Uint8Array;
  iv: Uint8Array;
  authTag: Uint8Array;
}

export interface MLKEMKeyPair {
  privateKey: Uint8Array;
  publicKey: Uint8Array;
}

export interface EncapsulationResult {
  cipherText: Uint8Array;
  sharedSecret: Uint8Array;
}

export interface DecapsulationResult {
  cipherText: Uint8Array;
  sharedSecret: Uint8Array;
}

export interface RecipientKeyData {
  id: string;
  publicKey: Uint8Array;
}

export interface EncryptedRecipientKey {
  recipientId: string;
  cipherText: Uint8Array;
  encryptedFileKey: AesGcmEncryptionResult;
}

export interface EncryptedComponents {
  data: Uint8Array;
  iv: Uint8Array;
  authTag: Uint8Array;
}

export interface PasswordHashResult {
  sha512Hash: string;
  keyHex: string;
  encoded: string;
  hash: Uint8Array;
}

export interface HashResult {
  hash: Uint8Array;
  hexString: string;
}

export abstract class CryptoService {
  abstract generateAesKey(): string;
  abstract generateMlKemKeyPair(seed?: Uint8Array): Promise<MLKEMKeyPair>;
  abstract generateMlKemKeyPairFromCredentials(
    email: string,
    password: string
  ): Promise<MLKEMKeyPair>;
  abstract encryptPrivateKey(
    privateKey: Uint8Array,
    ukek: PasswordHashResult
  ): Promise<string>;
  abstract decryptPrivateKey(
    encryptedPrivateKeyHex: string,
    ukekHash: Uint8Array
  ): Promise<Uint8Array>;
  abstract encapsulateMlKem(
    publicKey: Uint8Array,
    seed?: Uint8Array
  ): EncapsulationResult;
  abstract decapsulateMlKem(
    cipherText: Uint8Array,
    privateKey: Uint8Array
  ): DecapsulationResult;
  abstract encrypt(
    data: Uint8Array,
    key: Uint8Array
  ): Promise<AesGcmEncryptionResult>;
  abstract decrypt(
    encryptedData: Uint8Array,
    iv: Uint8Array,
    authTag: Uint8Array,
    key: Uint8Array
  ): Promise<Uint8Array>;
  abstract hashSha256(
    data: File | Blob | ArrayBuffer | Uint8Array
  ): Promise<Uint8Array>;
  abstract hashSha512(input: string): Promise<HashResult>;
  abstract hashPassword(
    password: string,
    emailForSalt: string
  ): Promise<PasswordHashResult>;
  abstract deriveKey(password: string, email: string): Promise<string>;
  abstract randomBytes(length: number): Uint8Array;
}
